import React, { createContext, ReactNode, useContext } from "react";

import { trim } from "lodash";
import NextImage from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import VerifiedIcon from "public/icons/verified-2.svg";

import { Button } from "@/components/form/Button/Button";
import { SvgIcon } from "@/components/ui/svg-icon/SvgIcon";
import { Tooltip } from "@/components/ui/tooltip/Tooltip";
import { useLadyIdContext } from "@/context/LadyId.context";
import { useAuthContext } from "@/context/auth-renderer/Auth.context";
import { useFavorite } from "@/features/lady-profile/hooks";
import useChatWindow from "@/hooks/useChatOpen";
import { useOpenRegisterModal } from "@/hooks/useOpenRegisterModal";
import { TruncatedWithTooltip } from "@/lib/truncate/TruncatedWithTooltip";
import { LadyMinV2DTO } from "@/models/lady-dtos";

import { SideButtonsList, CardLabels } from "../components";
import { CardBottomButtonRenderer } from "../lady-card-v2-aspect-ratio/components/CardBottomButtonRenderer";

type LadyCardContextType = {
  ladyDto: LadyMinV2DTO;
  push: (url: string) => Promise<boolean>;
  isAuthenticated: boolean;
  openChatWindow: ReturnType<typeof useChatWindow>;
  openRegModalHandler: (
    header: string,
  ) => (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onRemove?: () => void;
  handleFavoriteClick: (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => Promise<void>;
  toggleFavoriteLady?: (id: number) => void;
};

const LadyCardContext = createContext<LadyCardContextType | null>(null);

interface LadyCardProps {
  ladyDto: LadyMinV2DTO;
  children: ReactNode;
  onRemove?: () => void;
  refetch?: () => void;
  toggleFavoriteLady?: (id: number) => void;
}

export const LadyCardV3_builder = ({
  ladyDto,
  children,
  onRemove,
  refetch,
  toggleFavoriteLady,
}: LadyCardProps) => {
  const { push } = useRouter();
  const { isAuthenticated } = useAuthContext();
  const openChatWindow = useChatWindow(ladyDto.id);
  const { openModalWithHeader } = useOpenRegisterModal({
    ladyDtoParam: ladyDto,
  });
  const { setLadyId } = useLadyIdContext();

  const { onFavouriteClick } = useFavorite({
    objectId: ladyDto.id,
    isFavorite: ladyDto.isFavorite,
    favoriteType: "LADY",
  });

  const handleFavoriteClick = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    await onFavouriteClick(e);
    refetch?.();
  };

  const openRegModalHandler =
    (header: string) =>
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      openModalWithHeader(header)();
      setLadyId(ladyDto.id.toString());
    };

  const value: LadyCardContextType = {
    ladyDto,
    push,
    isAuthenticated,
    openChatWindow,
    openRegModalHandler,
    onRemove,
    handleFavoriteClick,
    toggleFavoriteLady,
  };

  return (
    <LadyCardContext.Provider value={value}>
      {children}
    </LadyCardContext.Provider>
  );
};

const useLadyCardContext = () => {
  const context = useContext(LadyCardContext);
  if (!context) {
    throw new Error("LadyCard sub-components must be used within a LadyCard");
  }
  return context;
};

LadyCardV3_builder.Container = function Container({
  children,
}: {
  children: ReactNode;
}) {
  const { ladyDto } = useLadyCardContext();

  return (
    <div
      className="flex flex-col items-center center"
      data-testid={`ladyCard ${ladyDto.id}`}
    >
      {children}
    </div>
  );
};

LadyCardV3_builder.ImageWrapper = function ImageWrapper({
  children,
  disableLink = false,
}: {
  children: ReactNode;
  disableLink?: boolean;
}) {
  const { ladyDto, push } = useLadyCardContext();
  const goToLadyPage = () => push(`/lady/id${ladyDto.id}`);
  return (
    <div className="relative rounded-lg overflow-clip text-white w-full">
      <div
        onClick={disableLink ? undefined : goToLadyPage}
        className={`relative block pt-[150%] ${
          disableLink ? "cursor-default" : "cursor-pointer"
        }`}
      >
        <LadyCardV3_builder.Image />
        {children}
      </div>
    </div>
  );
};

LadyCardV3_builder.Image = function Image() {
  const { ladyDto } = useLadyCardContext();
  return (
    <NextImage
      data-testid="cardAvatar"
      fill
      sizes="200px"
      src={ladyDto.url || ladyDto.photoSrcs.medium}
      className="object-cover absolute"
      unoptimized
      alt={trim(
        `${ladyDto.age} y.o. ${ladyDto.firstName} from ${ladyDto.from} - ID ${ladyDto.id}`,
      )}
    />
  );
};

LadyCardV3_builder.SideButtons = function SideButtons({
  skipFavoriteButton = false,
}: {
  skipFavoriteButton?: boolean;
}) {
  const { ladyDto, openRegModalHandler, toggleFavoriteLady } =
    useLadyCardContext();

  return (
    <SideButtonsList
      skipFavoriteButton={skipFavoriteButton}
      ladyDto={ladyDto}
      onClickHandler={openRegModalHandler}
      toggleFavoriteLady={toggleFavoriteLady}
    />
  );
};

LadyCardV3_builder.DefaultBottomButton = function DefaultBottomButton() {
  const { ladyDto, openRegModalHandler } = useLadyCardContext();

  return (
    <CardBottomButtonRenderer
      openRegModalHandler={openRegModalHandler}
      isOnline={ladyDto.isOnline}
      isVideochatAvailable={ladyDto.isVideochatAvailable}
      ladyId={ladyDto.id}
      hasIntroVideo={ladyDto.hasIntroVideo}
    />
  );
};

LadyCardV3_builder.RemoveBottomButton = function RemoveBottomButton() {
  const { handleFavoriteClick, onRemove } = useLadyCardContext();

  const handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();

    if (onRemove) {
      onRemove();
    } else if (handleFavoriteClick) {
      handleFavoriteClick(e);
    }
  };
  const buttonLabel = onRemove
    ? "Remove from Blacklist"
    : "Remove from Favorites";
  return (
    <Button
      variant="primary"
      onClick={handleClick}
      size="large"
      onHoverVariant="gradient-flip"
      aria-label={buttonLabel}
      className="absolute bottom-2 left-1/2 transform -translate-x-1/2 w-[90%] text-base font-bold"
    >
      remove
    </Button>
  );
};

LadyCardV3_builder.Labels = function Labels() {
  const { ladyDto } = useLadyCardContext();
  return (
    <CardLabels
      birthday={ladyDto.birthday}
      isBirthdaySoon={ladyDto.isBirthdaySoon}
      isNew={ladyDto.isNewProfile}
      hasIntroVideo={
        ladyDto.hasIntroVideo && true
        // FIXME: restore this
        //!isFeatureFlagEnabled("LADY_CARD_INTRO_BADGE_HIDDEN")
      }
    />
  );
};

LadyCardV3_builder.Info = function Info({
  showId = false,
  disableLink = false,
}: {
  showId?: boolean;
  disableLink?: boolean;
}) {
  const { ladyDto } = useLadyCardContext();

  const nameAndAgeContent = (
    <>
      <TruncatedWithTooltip
        data-testid="cardName"
        className="text-sm sm:text-base font-bold mt-2 inline-block"
      >
        {trim(ladyDto.firstName)}
      </TruncatedWithTooltip>
      <span
        data-testid="cardAge"
        className="text-sm sm:text-base font-bold mt-2 inline-block"
      >
        ,&nbsp;{ladyDto.age}
      </span>
    </>
  );

  return (
    <>
      <div className="flex items-end gap-1 max-w-full">
        {disableLink ? (
          <div className="flex max-w-full shrink min-w-0">
            {nameAndAgeContent}
          </div>
        ) : (
          <Link
            href={`/lady/id${ladyDto.id}`}
            className="flex max-w-full shrink min-w-0"
            prefetch={false}
          >
            {nameAndAgeContent}
          </Link>
        )}
        <Tooltip title="Verified">
          <SvgIcon className="w-4 h-5 block">
            <VerifiedIcon />
          </SvgIcon>
        </Tooltip>
      </div>
      <div
        data-testid="cardLocation"
        className="text-center text-grey1-700 sm:text-sm text-xs"
      >
        <span>{ladyDto.from.trim()}</span>
      </div>
      {showId && (
        <span
          data-testid="cardId"
          className="text-grey1-700 sm:text-xs text-xs inline-block"
        >
          ID: {ladyDto.id}
        </span>
      )}
    </>
  );
};
